import listProjectMembershipsForCurrentUser from '@/graphql/queries/ListProjectMembershipsForCurrentUser'
import ListWorkflows from '@/graphql/queries/ListWorkflows'
import OnCreateTeamMemberForUser from '@/graphql/subscriptions/OnCreateTeamMemberForUser'
import OnDeleteCurrentTeamMember from '@/graphql/subscriptions/OnDeleteCurrentTeamMember'
import UpdateTeamMemberLastLogin from '@/graphql/mutations/UpdateTeamMemberLastLogin'
import UpdateTeamMember from '@/graphql/mutations/UpdateTeamMember'
import client from '@/graphql/client'
import { addToSubscriptions } from '@/api/subscriptionHelpers'

export const projectMemberships = {
  namespaced: true,
  state: {
    projectMemberships: null,
    activeProjectId: null,
    subscriptions: null
  },
  mutations: {
    setProjectMemberships (state, payload) {
      state.projectMemberships = payload
    },
    setSubscriptions (state, payload) {
      state.subscriptions = payload
    },
    unsubscribeFromAll (state) {
      if (state.subscriptions) {
        for (const subscription of state.subscriptions) {
          subscription.unsubscribe()
        }
      }
      state.subscriptions = null
    },
    addProjectMembership (state, payload) {
      const index = state.projectMemberships.findIndex(membership => membership.id === payload.id)
      if (index === -1) {
        state.projectMemberships.unshift(payload)
      }
    },
    deleteProjectMembership (state, payload) {
      state.projectMemberships = state.projectMemberships.filter(membership => membership.project?.id !== payload.projectId)
    },
    updateProject (state, payload) {
      const index = state.projectMemberships.findIndex(membership => membership.project?.id === payload.id)
      state.projectMemberships[index].project = payload
    },
    updateProjectMembership (state, { projectId, id, type }) {
      const membership = state.projectMemberships.find(m => m.project?.id === projectId)
      if (!membership) return

      const workflows = [...(membership.project?.workflows || [])]
      const index = workflows.findIndex(wf => wf.id === id)

      if (type === 'add' && index === -1) {
        workflows.push({ id })
        membership.workflowOrder = [...(membership.workflowOrder || []), id]
        membership.starredWorkflowOrder = [...(membership.starredWorkflowOrder || []), id]
      } else if (type === 'delete' && index >= 0) {
        workflows.splice(index, 1)
        membership.workflowOrder = (membership.workflowOrder || []).filter(wfId => wfId !== id)
        membership.starredWorkflowOrder = (membership.starredWorkflowOrder || []).filter(wfId => wfId !== id)
      }

      membership.project = { ...membership.project, workflows }
    },
    updateTeamMemberLastLogin (state, payload) {
      const index = state.projectMemberships.findIndex(membership => membership.id === payload.id)
      if (index >= 0) {
        state.projectMemberships[index] = { ...state.projectMemberships[index], lastLogin: payload.lastLogin }
      }
    },
    updateTeamMemberWorkflowOrder (state, { id, workflowOrder, starredWorkflowOrder }) {
      const index = state.projectMemberships.findIndex(membership => membership.id === id)
      if (index >= 0) {
        const updated = { ...state.projectMemberships[index] }
        if (workflowOrder !== undefined) updated.workflowOrder = workflowOrder
        if (starredWorkflowOrder !== undefined) updated.starredWorkflowOrder = starredWorkflowOrder
        state.projectMemberships[index] = updated
      }
    },
    setActiveProjectId: (state, payload) => {
      state.activeProjectId = payload
    },
    updateProjectOwner (state, payload) {
      const index = state.projectMemberships.findIndex(membership => membership.project?.id === payload.id)
      if (index >= 0) {
        state.projectMemberships[index].project = {
          ...state.projectMemberships[index].project,
          organisation: payload.organisation,
          organisationId: payload.organisation.id
        }
      }
    }
  },
  actions: {
    async updateTeamMemberLastAccessToProject ({ commit, state }, { projectId }) {
      const index = state.projectMemberships?.findIndex(membership => membership.project?.id === projectId)
      if (index > -1) {
        const teamMemberId = state.projectMemberships[index].id
        try {
          const res = await client.query({
            operationName: 'updateTeamMemberLastLogin',
            query: UpdateTeamMemberLastLogin,
            variables: {
              id: teamMemberId,
              projectId
            }
          })
          commit('updateTeamMemberLastLogin', res.updateTeamMemberLastLogin)
        } catch (e) {
          console.log(e)
        }
      }
    },

    async updateProjectMembership ({ commit, state }, { projectId, id, type }) {
      commit('updateProjectMembership', { projectId, id, type })
      const membership = state.projectMemberships?.find(m => m.project?.id === projectId)
      if (!membership) return
      try {
        await client.query({
          operationName: 'updateTeamMember',
          query: UpdateTeamMember,
          variables: { id: membership.id, projectId, workflowOrder: membership.workflowOrder || [], starredWorkflowOrder: membership.starredWorkflowOrder || [] }
        })
      } catch (e) {
        console.log(e)
      }
    },

    async updateWorkflowOrder ({ commit, state }, { projectId, workflowOrder, starredWorkflowOrder }) {
      const membership = state.projectMemberships?.find(m => m.project?.id === projectId)
      if (!membership) return
      const variables = {
        id: membership.id,
        projectId,
        workflowOrder: workflowOrder !== undefined ? workflowOrder : (membership.workflowOrder || []),
        starredWorkflowOrder: starredWorkflowOrder !== undefined ? starredWorkflowOrder : (membership.starredWorkflowOrder || [])
      }
      try {
        await client.query({
          operationName: 'updateTeamMember',
          query: UpdateTeamMember,
          variables
        })
        const commitPayload = { id: membership.id }
        if (workflowOrder !== undefined) commitPayload.workflowOrder = workflowOrder
        if (starredWorkflowOrder !== undefined) commitPayload.starredWorkflowOrder = starredWorkflowOrder
        commit('updateTeamMemberWorkflowOrder', commitPayload)
        commit('snackbar/showMessage', { content: 'Updated' }, { root: true })
      } catch (e) {
        console.log(e)
      }
    },

    async listProjectMembershipsForCurrentUser ({ commit, rootState }) {
      const res = await client.query({
        operationName: 'listProjectMembershipsForCurrentUser',
        query: listProjectMembershipsForCurrentUser
      })
      const rawMemberships = res.listProjectMembershipsForCurrentUser.filter((item) => item.project)

      await Promise.all(rawMemberships.map(async (item) => {
        const workflows = item.project.workflows
        if (!workflows?.nextToken) return

        let nextToken = workflows.nextToken
        while (nextToken) {
          const pageRes = await client.query({
            operationName: 'listWorkflows',
            query: ListWorkflows,
            variables: { projectId: item.project.id, limit: 100, nextToken }
          }, { failSilently: true })

          const page = pageRes?.listWorkflows?.project?.workflows
          workflows.items = (workflows.items || []).concat(page?.items || [])
          nextToken = page?.nextToken
        }
      }))

      const memberships = rawMemberships.map((item) => ({
        ...item,
        project: {
          ...item.project,
          workflows: item.project.workflows?.items || []
        }
      }))
      commit('setProjectMemberships', memberships)

      // Unsubscribe from previous subscriptions before creating new ones
      commit('unsubscribeFromAll')

      const subscriptions = []
      const addSubscription = (query, variables, callback) => {
        const sub = addToSubscriptions(query, variables, callback)
        subscriptions.push(sub)
      }

      addSubscription(OnCreateTeamMemberForUser, {
        userId: rootState.userId
      }, (data) => {
        commit('addProjectMembership', data.onCreateTeamMember.createTeamMemberFunction)
      })

      addSubscription(OnDeleteCurrentTeamMember, {
        userId: rootState.userId
      }, (data) => {
        commit('deleteProjectMembership', data.onDeleteCurrentTeamMember)
      })

      commit('setSubscriptions', subscriptions)
    }
  },
  getters: {
    activeProject: (state) => {
      if (!state.activeProjectId) return null
      return state.projectMemberships?.find(it => it.project?.id === state.activeProjectId)?.project
    },
    myProjects (state, getters, rootState) {
      const res = []
      for (let i = 0; i < state.projectMemberships.length; i++) {
        if (state.projectMemberships[i].project?.organisation.userId === rootState.userId) {
          const obj = Object.assign({}, state.projectMemberships[i].project)
          res.push(obj)
        }
      }
      return res
    }
  }
}
